/* Image loader - every photo on the page loads in the background right after the first view (SCENTREAT 30/09/2026;
   owner, all stores: "đừng đợi tới rồi mới load"; "làm tốt nhất sao cho trên phone mọi thứ phải nhanh và hình cũng
   phải rõ nha... trên desktop mình thấy ổn r"). Adapted from Back Hero's assets/bh-img-loader.js; v2 (30/09 evening)
   ports WakaPaw's measured improvements (assets/wk-img-loader.js) into the phone path.
   Self-contained, loaded from layout/theme.liquid with <script src async>. Remove that line to switch it off.

   PHONES / TABLETS (CFG.loadAllWhen):
   1. Starts when the HTML is parsed (readyState "interactive"), not after every deferred / module script. Then it waits
      for the first view: img[fetchpriority="high"] (the main product photo), else the largest image in the first screen
      (the home hero video's poster), else 300 ms; at the latest startCap after the HTML is parsed.
   2. Then EVERY <img loading="lazy"> on the page loads in the background, nearest to the screen first, 5 at a time. An
      image with no box (other card slides, closed tabs) takes the place of its nearest laid-out container plus a
      penalty. Each one is fetched through a detached new Image() first; only once that copy has loaded is the real
      <img> switched to eager, so it paints from cache.
   3. While the shopper scrolls (the page or a rail) the queue starts nothing new; ~300 ms after the scrolling stops it re-sorts by
      distance from the new position. Swiping a rail loads its cards within one view either side at once.
   4. Look-ahead 1400 px: what the shopper is about to reach is switched to eager at once. A rail / slideshow / marquee:
      its laid-out slides in view (plus half a view either side) at once; its other slides stay in the queue, nearest
      first (all slides at once starved the ones on screen, measured at WakaPaw).
   5. Sharp but light: before an image has picked a file (no currentSrc) it gets sizes = its slot at 0.85x on 3x screens
      (a ~2.55x file; the owner wants at least ~2.5x) and finer srcset steps (400/480/560/640/720...) inside Shopify's
      352 -> 832 jump, never wider than the theme's own widths. Product gallery photos and product cards keep full 3x.
      Once an image has a file (currentSrc) its src / srcset / sizes are NEVER touched again: re-assigning them ("kick")
      left broken "?" images on the owner's iPhone at Back Hero (30/09). In Safari an image usually has its currentSrc
      from the start, so there this step simply does nothing.
   DESKTOP: unchanged from v1 — starts at DOMContentLoaded; only the 1400 px look-ahead runs (a group loads its laid-out
   slides at once), plus the image blocks / blog cards this change made lazy are queued after the first view (the full
   queue cost ~0.5 s desktop LCP); sizes="auto" is pinned to the real box before an image turns eager (what lazy did).
   Skipped: cart drawer, dialogs, menus, search, the header group. Images added later (reviews, recommendations,
   theme editor) join through a MutationObserver. */
(() => {
  const CFG = {
    maxInFlight: 5,
    loadAllWhen: '(max-width: 1024px), (pointer: coarse)',
    // desktop: only the images this change made lazy (they used to load eagerly with the page) are queued, right after
    // the first view, so desktop gets what it had before without them competing with the main image.
    desktopQueue: '.image-block__image, .resource-image__image',
    lookAhead: '1400px 0px',
    noHeroDelay: 300,
    startCap: 8000, // phones: ms after the HTML is parsed
    deskStartCap: 4000, // desktop: ms after DOMContentLoaded (v1)
    stuckMs: 8000,
    pauseMs: 300,
    // containers whose laid-out images load together once the container comes near
    groups: 'slideshow-slides, marquee-component, .st-moods__stage, .st-rv__track, [data-img-rail]',
    skip: '#cart-drawer, cart-drawer-component, dialog, [role="dialog"], .menu-drawer, header-drawer, predictive-search-component, .shopify-section-group-header-group, template, [data-st-img-skip]', // [data-st-img-skip]: closed home tabs' mood photos (QA 07/10), native lazy loads them when their tab opens
    scrollerQuery: '(min-width: 990px)',
    scrollerSel: '.page-wrapper',
    phone: '(max-width: 749px)',
    density: 0.85,
    // full 3x: product gallery photos (like slide 1) and product cards (their photos carry small text that looked soft
    // at 2.6x on Back Hero, 30/09)
    fullDensity: 'media-gallery, .product-media, product-card, .product-card',
    // phones: the ~2.55x sizing of step 5 (Chrome-type engines only in practice: an iPhone image has its file from the
    // start). false = phones only pin sizes="auto" to the box (what lazy loading itself would pick), like desktop.
    phoneSizing: true,
    steps: [300, 400, 480, 560, 640, 720, 800, 900, 1000, 1100],
  };
  if (window.__stImgLoader) return;
  window.__stImgLoader = true;

  const mq = matchMedia(CFG.scrollerQuery);
  const phone = matchMedia(CFG.phone);
  // 28/10/2026 owner 'mạng chậm load chậm lắm': on a slow connection (2G/3G, or data saver on) the phone does NOT pre-load every
  // photo in the background (that ate the bandwidth the first screen needs); it keeps only the 1400 px look-ahead, like desktop.
  const conn = navigator.connection || {};
  const slowNet = !!conn.saveData || /(^|-)(2g|3g)$/.test(conn.effectiveType || '');
  const loadAll = matchMedia(CFG.loadAllWhen).matches && !slowNet;
  const dense = (window.devicePixelRatio || 1) >= 2.5;
  const scroller = () => {
    const pw = mq.matches && document.querySelector(CFG.scrollerSel);
    return pw && /(auto|scroll)/.test(getComputedStyle(pw).overflowY) ? pw : null;
  };
  const view = () => { const sc = scroller(); return sc ? { top: sc.getBoundingClientRect().top, h: sc.clientHeight } : { top: 0, h: innerHeight }; };
  const lazyIn = (root) => [...(root.querySelectorAll ? root.querySelectorAll('img[loading="lazy"]') : [])].filter((i) => !i.closest(CFG.skip));
  const boxOf = (el) => {
    const r = el.getBoundingClientRect();
    return r.width > 2 && r.height > 2 ? r : null;
  };

  // ---- sizing (phones: ~2.55x; desktop: what lazy + sizes="auto" did). Only before the image has picked a file.
  const bareSizes = (img) => (img.getAttribute('sizes') || '').replace(/^\s*auto\s*,\s*/, '').trim();
  function slotWidth(img) {
    const r = boxOf(img);
    if (r) return r.width;
    // no box (other card slides, closed tabs): a laid-out image of the same kind in the same section
    const sec = img.closest('.shopify-section') || document;
    const key = bareSizes(img);
    for (const o of sec.querySelectorAll('img')) {
      if (o === img || bareSizes(o) !== key) continue;
      const b = boxOf(o);
      if (b) return b.width;
    }
    return 0;
  }
  function addSteps(img) {
    const set = img.getAttribute('srcset') || '';
    let list = set.split(',').map((s) => { const p = s.trim().split(/\s+/); return { url: p[0], w: parseInt(p[1], 10) }; }).filter((c) => c.url && c.w);
    if (list.length < 2 || !/\d+w/.test(set)) return;
    list.sort((a, b) => a.w - b.w);
    const min = list[0].w, max = list[list.length - 1].w, add = [];
    for (const w of CFG.steps) {
      if (w <= min || w >= max || list.some((c) => Math.abs(c.w - w) / w < 0.08)) continue;
      const from = list.find((c) => c.w > w);
      if (from && /[?&]width=\d+/.test(from.url)) add.push({ url: from.url.replace(/([?&])width=\d+/, `$1width=${w}`), w });
    }
    if (!add.length) return;
    list = list.concat(add).sort((a, b) => a.w - b.w);
    img.setAttribute('srcset', list.map((c) => `${c.url} ${c.w}w`).join(', '));
  }
  // Shopify's sizes="auto" polyfill for Safari parks src / srcset in data-auto-sizes-* until the first paint: never touch
  // those (the polyfill re-assigns src / srcset itself when it restores them)
  const parked = (img) => img.hasAttribute('data-auto-sizes-src') || img.hasAttribute('data-auto-sizes-srcset');
  function tune(img) {
    if (img.__stTuned || img.currentSrc || img.complete || parked(img)) return; // never once the image has a file
    img.__stTuned = 1;
    // media-with-content photos carry their own phone sizes (object-fit: cover aware, snippets/st-media-image.liquid)
    if (img.classList.contains('st-media-sized')) return;
    if (!/\d+w/.test(img.getAttribute('srcset') || '')) return;
    const auto = /^\s*auto\b/.test(img.getAttribute('sizes') || '');
    if (phone.matches && CFG.phoneSizing) {
      const w = slotWidth(img);
      if (!w) {
        if (auto) img.setAttribute('sizes', bareSizes(img) || '100vw');
        return;
      }
      addSteps(img);
      const k = dense && !img.closest(CFG.fullDensity) ? CFG.density : 1;
      img.setAttribute('sizes', `${Math.max(40, Math.ceil(w * k))}px`);
    } else if (auto) {
      const r = boxOf(img);
      if (r) img.setAttribute('sizes', `${Math.max(40, Math.ceil(r.width))}px`);
    }
  }
  const detached = (img) => {
    const pre = new Image();
    if (img.getAttribute('sizes')) pre.sizes = img.getAttribute('sizes');
    if (img.getAttribute('srcset')) pre.srcset = img.getAttribute('srcset');
    if (img.getAttribute('src')) pre.src = img.getAttribute('src');
    return pre;
  };

  // phones: lazy -> eager (sizing first while it has no file; after that only the loading attribute changes)
  function eager(img) {
    if (img.getAttribute('loading') !== 'lazy') return;
    tune(img);
    img.setAttribute('loading', 'eager');
    img.setAttribute('data-st-early', '');
  }
  // phones, queue: the same file through a detached copy first, then the real image turns eager (from cache)
  function warm(img) {
    return new Promise((res) => {
      let done = false;
      const fin = () => { if (done) return; done = true; clearTimeout(t); eager(img); res(); };
      const t = setTimeout(fin, CFG.stuckMs);
      if (img.getAttribute('loading') !== 'lazy' || (img.complete && img.naturalWidth)) return fin();
      tune(img);
      const pre = detached(img);
      pre.onload = fin; pre.onerror = fin;
      img.addEventListener('load', fin, { once: true });
    });
  }
  // desktop (v1, unchanged): lazy -> eager plus a detached copy that warms the cache
  function force(img) {
    if (img.getAttribute('loading') !== 'lazy' || img.complete) return false;
    tune(img);
    img.setAttribute('loading', 'eager');
    img.setAttribute('data-st-early', '');
    if (!img.complete) img.__stPre = detached(img);
    return true;
  }
  const settled = (img) =>
    new Promise((res) => {
      if (img.complete) return res();
      const pre = img.__stPre;
      let fin = false;
      const done = () => {
        if (fin) return;
        fin = true;
        clearTimeout(t);
        img.removeEventListener('load', done);
        img.removeEventListener('error', done);
        if (pre) pre.onload = pre.onerror = null;
        res();
      };
      const t = setTimeout(done, CFG.stuckMs);
      img.addEventListener('load', done);
      img.addEventListener('error', done);
      if (pre) {
        if (pre.complete) return done();
        pre.onload = pre.onerror = done;
      }
    });

  // ---- background queue, nearest to the screen first; ties keep DOM order
  let queue = [], inFlight = 0, running = false;
  function distance(img) {
    if (!loadAll) { const r = boxOf(img); return r ? (r.bottom < 0 ? -r.bottom : Math.max(0, r.top - innerHeight)) : 1e9; } // desktop: v1
    let el = img, pen = 0, r = img.getBoundingClientRect();
    while (el && !r.width && !r.height) { el = el.parentElement; pen = 1200; r = el ? el.getBoundingClientRect() : r; }
    if (!r.width && !r.height) return 1e9;
    const v = view();
    return pen + (r.bottom < v.top ? v.top - r.bottom : Math.max(0, r.top - v.top - v.h));
  }
  const byDistance = (list) => list.map((i) => [distance(i), i]).sort((a, b) => a[0] - b[0]).map((x) => x[1]);
  function enqueue(imgs) {
    const fresh = imgs.filter((i) => !i.__stq);
    if (!fresh.length) return;
    fresh.forEach((i) => { i.__stq = 1; });
    queue = byDistance(queue.concat(fresh));
    pump();
  }
  let pauseUntil = 0, resumeT = 0;
  function resume() {
    resumeT = 0;
    const wait = pauseUntil - performance.now();
    if (wait > 0) { resumeT = setTimeout(resume, wait + 20); return; }
    queue = byDistance(queue);
    pump();
  }
  let retryT = 0;
  function pump() {
    if (!running || performance.now() < pauseUntil) return;
    const later = [];
    while (inFlight < CFG.maxInFlight && queue.length) {
      const img = queue.shift();
      if (!img.isConnected || img.getAttribute('loading') !== 'lazy' || img.complete) continue;
      if (parked(img)) { later.push(img); continue; }
      inFlight++;
      (loadAll ? warm(img) : (force(img), settled(img))).then(() => { inFlight--; pump(); });
    }
    if (later.length) { queue = later.concat(queue); if (!retryT) retryT = setTimeout(() => { retryT = 0; pump(); }, 400); }
  }
  // phones: a group coming near — its laid-out slides in view (plus half a view either side) now; its other slides stay
  // in the queue at their own distance (a rail on screen: distance 0, so next in line). Not "all to the front" as
  // WakaPaw: here every product card is a slideshow group, and dozens of card slides at the front starved the page.
  function loadGroup(g) {
    const gr = g.getBoundingClientRect(), pad = gr.width / 2;
    lazyIn(g).forEach((i) => { const r = boxOf(i); if (r && r.right > gr.left - pad && r.left < gr.right + pad) eager(i); });
  }
  // phones: a rail being swiped — its laid-out cards within one view either side load at once
  function railAhead(sc) {
    if (sc.__stRaf) return;
    sc.__stRaf = requestAnimationFrame(() => {
      sc.__stRaf = 0;
      const g = sc.getBoundingClientRect(), px = g.width, py = g.height;
      lazyIn(sc).forEach((i) => { const r = boxOf(i); if (r && r.right > g.left - px && r.left < g.right + px && r.bottom > g.top - py && r.top < g.bottom + py) eager(i); });
    });
  }
  // any scroll (page or rail, as WakaPaw) pauses the queue; a rail swiped sideways also loads its next cards at once
  function onScroll(e) {
    const t = e.target;
    if (t && t.nodeType === 1 && t !== document.documentElement && t !== document.body && t !== scroller() && !t.closest(CFG.skip)) railAhead(t);
    pauseUntil = performance.now() + CFG.pauseMs;
    if (!resumeT) resumeT = setTimeout(resume, CFG.pauseMs + 20);
  }

  // ---- look-ahead (everywhere)
  let io = null;
  function observe(root) {
    if (!io) return;
    const q = root.querySelectorAll ? root : document;
    if (root.matches && root.matches(CFG.groups) && !root.closest(CFG.skip)) io.observe(root);
    q.querySelectorAll(CFG.groups).forEach((g) => { if (!g.closest(CFG.skip)) io.observe(g); });
    lazyIn(q).forEach((i) => { if (!i.closest(CFG.groups)) io.observe(i); });
  }
  function makeObserver() {
    if (!('IntersectionObserver' in window)) return;
    if (io) io.disconnect();
    io = new IntersectionObserver((es) => es.forEach((en) => {
      if (!en.isIntersecting) return;
      const t = en.target;
      io.unobserve(t);
      if (!loadAll) { if (t.tagName === 'IMG') force(t); else lazyIn(t).filter(boxOf).forEach(force); return; } // desktop: v1
      if (t.tagName === 'IMG') eager(t);
      else loadGroup(t);
    }), { root: scroller(), rootMargin: CFG.lookAhead });
    observe(document);
  }

  const queueable = (imgs) => (loadAll ? imgs : imgs.filter((i) => i.matches(CFG.desktopQueue)));
  function start() {
    if (running) return;
    running = true;
    window.__stImgLoaderStart = Math.round(performance.now()); // for the before/after measurements
    makeObserver();
    if (mq.addEventListener) mq.addEventListener('change', makeObserver);
    else if (mq.addListener) mq.addListener(makeObserver);
    if (loadAll) document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    enqueue(queueable(lazyIn(document)));
    watchLate();
  }
  function watchLate() {
    let pending = [], scheduled = false;
    new MutationObserver((ms) => {
      for (const m of ms) for (const n of m.addedNodes) if (n.nodeType === 1 && n.tagName !== 'SCRIPT' && n.tagName !== 'LINK') pending.push(n);
      if (!pending.length || scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        const nodes = pending;
        pending = [];
        const imgs = [];
        nodes.forEach((n) => {
          if (!n.isConnected) return;
          observe(n);
          if (n.tagName === 'IMG' && n.getAttribute('loading') === 'lazy' && !n.closest(CFG.skip)) imgs.push(n);
          imgs.push(...lazyIn(n));
        });
        enqueue(queueable(imgs));
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  // ---- first view first
  function firstView() {
    const hi = document.querySelector('img[fetchpriority="high"]');
    if (hi && boxOf(hi)) return hi;
    let best = null, area = 0;
    for (const i of document.querySelectorAll('img')) {
      if (i.getAttribute('loading') === 'lazy' || i.closest(CFG.skip)) continue;
      const r = boxOf(i);
      if (!r || r.top > innerHeight || r.bottom < 0) continue;
      if (r.width * r.height > area) { area = r.width * r.height; best = i; }
    }
    return area > innerWidth * innerHeight * 0.08 ? best : null;
  }
  function afterFirstView(cap) {
    const first = firstView();
    if (!first) setTimeout(start, CFG.noHeroDelay);
    else if (first.complete) start(); // synchronously: a setTimeout(0) would wait for every deferred / module script
    else { first.addEventListener('load', start, { once: true }); first.addEventListener('error', start, { once: true }); }
    setTimeout(start, cap);
  }
  if (loadAll) {
    // phones: as soon as the HTML is parsed (not DOMContentLoaded, which waits for every deferred / module script)
    if (document.readyState !== 'loading') afterFirstView(CFG.startCap);
    else document.addEventListener('readystatechange', function h() { if (document.readyState === 'loading') return; document.removeEventListener('readystatechange', h); afterFirstView(CFG.startCap); });
  } else {
    // desktop: never before DOMContentLoaded, as v1 (loaded defer). This async file can run after the HTML is parsed
    // but before DOMContentLoaded (readyState is "interactive" in both), so ask the navigation timing which it is.
    const nav = performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
    const dclDone = document.readyState === 'complete' || (nav ? nav.domContentLoadedEventStart > 0 : document.readyState !== 'loading');
    if (dclDone) afterFirstView(CFG.deskStartCap);
    else document.addEventListener('DOMContentLoaded', () => afterFirstView(CFG.deskStartCap), { once: true });
  }
})();
